import { useMemo } from 'react'
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import { formatTokenCount } from '@/lib/formatTokenCount'
import type { TokenUsagePeriod } from '@/types/analytics'
import { formatPeriodLabel } from './DataOverTimeChart'

interface TokensOverTimeChartProps {
  data: TokenUsagePeriod[]
  dateRangeLabel: string
  height?: number
}

const chartConfig = {
  totalTokens: {
    label: 'Total',
    color: 'hsl(var(--chart-1))',
  },
  inputTokens: {
    label: 'Input',
    color: 'hsl(var(--chart-2))',
  },
  outputTokens: {
    label: 'Output',
    color: 'hsl(var(--chart-3))',
  },
} satisfies ChartConfig

const chartClassNames = {
  card: 'flex-1 rounded-2xl shadow-sm',
  header: 'pb-2',
  title: 'text-base font-semibold leading-4',
  subtitle: 'text-sm text-muted-foreground',
  content: 'pt-0',
}

export function TokensOverTimeChart({
  data,
  dateRangeLabel,
  height = 250,
}: TokensOverTimeChartProps) {
  const chartData = useMemo(
    () =>
      data.map((item) => ({
        ...item,
        label: formatPeriodLabel(item.period),
      })),
    [data]
  )

  return (
    <Card className={chartClassNames.card}>
      <CardHeader className={chartClassNames.header}>
        <CardTitle className={chartClassNames.title}>
          Tokens over time
        </CardTitle>
        <p className={chartClassNames.subtitle}>{dateRangeLabel}</p>
      </CardHeader>
      <CardContent className={chartClassNames.content}>
        <ChartContainer
          config={chartConfig}
          className={`h-[${height}px] w-full`}
        >
          <LineChart data={chartData} accessibilityLayer>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={formatTokenCount}
            />
            <ChartTooltip content={<ChartTooltipContent />} />
            <ChartLegend content={<ChartLegendContent />} />
            <Line
              type="monotone"
              dataKey="totalTokens"
              stroke="var(--color-totalTokens)"
              strokeWidth={3}
              dot={{ fill: 'var(--color-totalTokens)', r: 3 }}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey="inputTokens"
              stroke="var(--color-inputTokens)"
              strokeWidth={2}
              dot={{ fill: 'var(--color-inputTokens)', r: 3 }}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey="outputTokens"
              stroke="var(--color-outputTokens)"
              strokeWidth={2}
              dot={{ fill: 'var(--color-outputTokens)', r: 3 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
